import { compactI18n, parseI18n } from '@src/i18n';

import { orderPlatformsProductionFirst } from '../helpers';
import { clientCounts, isParent, reachedPlatformCount } from '../shared/clientCounts';
import { RESTRICTED } from './accessRules';

// Language codes whose text is filled in an override text (JSON i18n string, or null).
export const overriddenLanguages = (text) => Object.keys(compactI18n(parseI18n(text)));

// Language codes whose text differs between two override texts (JSON i18n strings, or null), over the filled
// languages of both: those of `from`, then those that only `to` fills.
export const changedLanguages = (from, to) => {
    const before = compactI18n(parseI18n(from));
    const after = compactI18n(parseI18n(to));
    const codes = [...new Set([...Object.keys(before), ...Object.keys(after)])];
    return codes.filter((code) => before[code] !== after[code]);
};

// Override text to send from the texts of the form, keyed by language: null when no language is filled.
export const toOverrideText = (texts) => {
    const filled = compactI18n(texts);
    return Object.keys(filled).length > 0 ? JSON.stringify(filled) : null;
};

// A row that neither overrides a text nor sets the visibility: the target is only listed.
export const isEmptyRow = ({ title, description, visible }) => title === null && description === null && visible === null;

// Body of PUT /client-overrides: the whole row, with `changes` applied (an explicit null is kept).
export const rowPayload = (override, changes = {}) => {
    const { title, description, visible } = { ...override, ...changes };
    return { title, description, visible };
};

const clientTarget = (client) => ({ kind: 'client', uuid: client.uuid, name: client.name });
const platformTarget = (platform) => ({ kind: 'platform', uuid: platform.uuid, name: platform.url });

// Target of a row, in the shape of the targets added by the search: the client by its name, the platform by its url.
export const targetOf = ({ client, platform }) => (platform ? platformTarget(platform) : clientTarget(client));

// Key of a target, unique among the clients and platforms: `kind:uuid`.
export const targetKey = (target) => `${target.kind}:${target.uuid}`;

// Data of the label of a row's target: its client with its counts, and the url of the platform for a platform row.
export const targetDescription = ({ client, platform }) => ({
    name: client.name,
    parentName: client.parent?.name ?? null,
    counts: clientCounts(client),
    url: platform?.url ?? null,
});

// What a row applies to: one platform, a parent with its platforms and sub-clients, or a client's platforms.
export const appliesTo = ({ client, platform }) => {
    if (platform) return { kind: 'platform', url: platform.url, clientName: client.name };
    if (isParent(client)) {
        return { kind: 'parent', name: client.name, platformCount: reachedPlatformCount(client), subClientCount: client.subClientCount };
    }
    return { kind: 'client', name: client.name, platformCount: client.platformCount };
};

// Keys of the `targets` whose request was fulfilled and of those whose request was rejected, from the `results`
// of Promise.allSettled, in the order of the targets.
export const splitSettled = (targets, results) => {
    const keysWith = (status) => targets.filter((_, index) => results[index].status === status).map(targetKey);
    return { added: keysWith('fulfilled'), failed: keysWith('rejected') };
};

// Rows that the client row `override` does not cover, as they hold their own: those of the client's platforms,
// and those of its sub-clients and of their platforms, in the order of `overrides`. A platform row covers nothing more.
export const moreSpecificRows = (override, overrides) => {
    if (override.platform) return [];
    const { uuid } = override.client;
    return overrides.filter((other) => (other.client.uuid === uuid && Boolean(other.platform)) || other.client.parent?.uuid === uuid);
};

const isAuthorized = (override) => override.visible === true;

// Rows of the table: every row of a public collection, the authorized rows of a restricted one.
export const tableRows = (overrides, visibility) => (visibility === RESTRICTED ? overrides.filter(isAuthorized) : overrides);

// Rows of a restricted collection that keep texts without giving access; none in a public collection.
export const withoutAccessRows = (overrides, visibility) =>
    visibility === RESTRICTED ? overrides.filter((override) => !isAuthorized(override)) : [];

const clientRowOf = (overrides, uuid) => overrides.find((override) => !override.platform && override.client.uuid === uuid) ?? null;

// Clients that the platform row `row` can move to, in order: its client, then the parent of a sub-client. Each
// option holds its `target`, whether it `isParent`, its `client` for the label (the client of the row with its
// counts; the parent from its own row when it has one, else by its uuid and name), its `currentRow` in
// `overrides` or null, the `changes` that the transfer makes on that row, as `{ field, from, to, languages }`
// (a text changes when one of its `languages` does, see changedLanguages; the display has none), and, for the
// parent, `shadowedBy`: the name of the sub-client whose own row will apply to the platform instead, or null.
export const transferOptions = (row, overrides) => {
    const changesOf = (currentRow) => {
        if (!currentRow) return [];
        const texts = ['title', 'description']
            .map((field) => ({
                field,
                from: currentRow[field],
                to: row[field],
                languages: changedLanguages(currentRow[field], row[field]),
            }))
            .filter(({ languages }) => languages.length > 0);
        const display =
            currentRow.visible === row.visible ? [] : [{ field: 'visible', from: currentRow.visible, to: row.visible, languages: [] }];
        return [...texts, ...display];
    };
    const option = (client, parent, shadowedBy) => {
        const currentRow = clientRowOf(overrides, client.uuid);
        return { target: clientTarget(client), isParent: parent, client, currentRow, changes: changesOf(currentRow), shadowedBy };
    };

    const { client } = row;
    if (!client.parent) return [option(client, false, null)];
    const parent = clientRowOf(overrides, client.parent.uuid)?.client ?? { uuid: client.parent.uuid, name: client.parent.name };
    return [option(client, false, null), option(parent, true, clientRowOf(overrides, client.uuid) ? client.name : null)];
};

// Whether a target sees the collection under the row that applies to it: in a public collection unless the row
// hides it, in a restricted one only when the row authorizes it. Without a row, the scope applies.
const seesCollection = (applicable, visibility) =>
    visibility === RESTRICTED ? applicable?.visible === true : applicable?.visible !== false;

// Row of `overrides` that applies to the platforms of `client` without their own row: its own row, else that of its parent.
const clientApplicableRow = (overrides, client) =>
    clientRowOf(overrides, client.uuid) ?? (client.parent ? clientRowOf(overrides, client.parent.uuid) : null);

// Access to the collection, under `visibility`, before and after the transfer of the platform row `row` to the
// `option` of transferOptions: `client`, for the platforms of the chosen client without their own row, whose
// row then holds the values of `row`; `platform`, for the platform of `row`, whose own row is then deleted, with
// `appliedBy`, the name of the client whose row then applies to it. Each one is `{ before, after }`.
export const transferAccess = (row, option, overrides, visibility) => {
    const moved = { client: option.client, platform: null, title: row.title, description: row.description, visible: row.visible };
    const replaced = (override) =>
        override.platform ? override.platform.uuid === row.platform.uuid : override.client.uuid === option.client.uuid;
    const after = [...overrides.filter((override) => !replaced(override)), moved];
    const platformRow = clientApplicableRow(after, row.client);
    return {
        client: {
            before: seesCollection(clientApplicableRow(overrides, option.client), visibility),
            after: seesCollection(moved, visibility),
        },
        platform: {
            before: seesCollection(row, visibility),
            after: seesCollection(platformRow, visibility),
            appliedBy: platformRow?.client.name ?? null,
        },
    };
};

// `pending` keys without one occurrence of each of `keys`: two requests on the same target each hold their own
// occurrence, so that the first one to settle does not release the other.
export const releaseKeys = (pending, keys) =>
    keys.reduce((rest, key) => {
        const index = rest.indexOf(key);
        return index === -1 ? rest : [...rest.slice(0, index), ...rest.slice(index + 1)];
    }, pending);

// Tree of a client read with its platforms and its sub-clients (GET /clients/{uuid} with `withPlatforms` and
// `withSubClients`): the client, its platforms (production first), then each sub-client followed by its
// platforms. A target with its own row of `overrides` is 'own', with that row; any other one is 'covered', with
// the name of the row that applies to it today: the client, or for the platform of a sub-client, the sub-client
// when it has its own row.
export const subTargetEntries = (client, overrides) => {
    const clientRow = (uuid) => overrides.find((override) => !override.platform && override.client.uuid === uuid);
    const platformRow = (uuid) => overrides.find((override) => override.platform?.uuid === uuid);
    const entry = (target, entity, level, row, coveredBy) =>
        row ? { target, entity, level, state: 'own', row } : { target, entity, level, state: 'covered', coveredBy };
    const platformEntries = (platforms, level, coveredBy) =>
        orderPlatformsProductionFirst(platforms).map((platform) =>
            entry(platformTarget(platform), platform, level, platformRow(platform.uuid), coveredBy),
        );

    return [
        { target: clientTarget(client), entity: client, level: 1, state: 'source' },
        ...platformEntries(client.platforms, 2, client.name),
        ...client.subClients.flatMap((subClient) => {
            const row = clientRow(subClient.uuid);
            return [
                entry(clientTarget(subClient), subClient, 2, row, client.name),
                ...platformEntries(subClient.platforms, 3, row ? subClient.name : client.name),
            ];
        }),
    ];
};
